import { useEffect } from 'react';
import { useBenchmarkProgress } from '../../hooks/useBenchmarkProgress';

interface BenchmarkProgressBarProps {
  runId: string;
  onComplete?: () => void;
}

export function BenchmarkProgressBar({ runId, onComplete }: BenchmarkProgressBarProps) {
  const { progress, error } = useBenchmarkProgress(runId);

  // Call onComplete when status changes to 'completed'
  useEffect(() => {
    if (progress?.status === 'completed' && onComplete) {
      onComplete();
    }
  }, [progress?.status, onComplete]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded p-4">
        <p className="text-red-800">Erreur: {error}</p>
      </div>
    );
  }

  if (!progress) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded p-4">
        <p className="text-gray-600">Chargement du statut...</p>
      </div>
    );
  }

  if (progress.status === 'completed') {
    return (
      <div className="bg-green-50 border border-green-200 rounded p-4">
        <p className="text-green-800 font-medium">✓ Benchmark terminé!</p>
      </div>
    );
  }

  if (progress.status === 'failed') {
    return (
      <div className="bg-red-50 border border-red-200 rounded p-4">
        <p className="text-red-800 font-medium">✕ Benchmark échoué</p>
      </div>
    );
  }

  if (progress.status === 'cancelled') {
    return (
      <div className="bg-orange-50 border border-orange-200 rounded p-4">
        <p className="text-orange-800 font-medium">⏹ Benchmark annulé</p>
        <p className="text-orange-600 text-sm mt-1">
          {progress.processed_documents}/{progress.total_documents} documents traités
        </p>
      </div>
    );
  }

  const percentage = progress.percentage;
  const barWidth = `${percentage}%`;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded p-4">
      <div className="mb-2">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium text-blue-900">🔄 Benchmark en cours...</span>
          <span className="text-sm font-medium text-blue-900">{percentage.toFixed(0)}%</span>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-blue-200 rounded-full h-4 overflow-hidden">
          <div
            className="bg-primary h-full transition-all duration-300"
            style={{ width: barWidth }}
          />
        </div>

        <div className="text-sm text-blue-700 mt-1">
          {progress.processed_documents}/{progress.total_documents} documents
        </div>
      </div>

      {/* Last processed */}
      {progress.last_processed && (
        <div className="text-sm text-blue-700 mt-2">
          ✓ Dernier traité: {progress.last_processed.filename} ({(progress.last_processed.processing_time_ms / 1000).toFixed(1)}s)
        </div>
      )}

      {/* Current document */}
      {progress.current_document && (
        <div className="text-sm text-blue-700">
          ⏳ En cours: {progress.current_document}...
        </div>
      )}

      {/* Estimated remaining time */}
      <div className="text-sm text-blue-700 mt-1">
        ⏱️ Temps estimé restant: {progress.estimated_remaining_seconds !== undefined && progress.estimated_remaining_seconds !== null
          ? `~${progress.estimated_remaining_seconds}s`
          : 'calcul en cours...'}
      </div>
    </div>
  );
}
